import NodeViewConstructorArgs from '../constructor/NodeViewConstructorArgs';
import AbstractNodeView from './AbstractNodeView';
import * as d3 from 'd3';
import action from '../assets/action.svg';
import api from '../assets/api.svg';
import discord from '../assets/discord.svg';
import email from '../assets/email.svg';
import jira from '../assets/jira.svg';
import slack from '../assets/slack.svg';
import microsoftTeams from '../assets/microsoftTeams.svg';
import WorkFlowView from '../view/WorkFlowView';
import View from '../view/View';
import { Syntax, Token } from '@trlogic/ui-pack';
import { NodeType } from '../../../../domain/collection/rule/FlowDomains';
import actionDisable from '../assets/action_disable.svg';
import { ActionJsonModel } from '../../../../domain/collection/rule/action/ActionDomains';
import NodeMouseEvent from '../support/NodeMouseEvent';
import ComponentUtil from '../../../../util/ComponentUtil';
import ComponentName from '../../../../constant/ComponentName';
import WorkflowLayoutUtil from '../../../../util/WorkflowLayoutUtil';

class ActionNodeView extends AbstractNodeView {
  private mainNode: d3.Selection<any, any, any, any> | undefined;
  private menuOpen: boolean = false;

  public constructor(args: NodeViewConstructorArgs) {
    super(args);
  }

  public initialize(): void {
    this.render();
  }

  public render(): void {
    let node: any;
    const status = (this.getModel() as any).value.status;
    const icon = (this.getModel() as any).value.icon;

    if (this.shadow) {
      node = d3.select((this.workFlowView as WorkFlowView).getParent()).append('svg');
    } else {
      node = d3
        .select((this.workFlowView as WorkFlowView).getNode())
        .select('#workflow-container-group')
        .append('g');
    }

    this.mainNode = node
      .attr('id', this.el.substring(1, this.el.length))
      .attr('type', 'Action')
      .attr('class', 'action_container')
      .style('position', 'absolute')
      .style('pointer-events', 'auto')
      .style('width', '50px')
      .style('height', '50px')
      .style('cursor', 'pointer');

    this.mainNode
      ?.append('rect')
      .attr('width', 50)
      .attr('height', 50)
      .attr('rx', 3)
      .style('stroke', '#ADD8E6')
      .attr('stroke-width', 1)
      .style('fill', 'white')
      .style('stroke-dasharray', '0');

    this.mainNode
      ?.append('svg:image')
      .attr('xlink:href', this.getIcon((this.getModel().value as any).icon))
      .attr('width', 20)
      .attr('height', 20)
      .attr('x', 50 / 2 - 10)
      .attr('y', 50 / 2 - 10);
    this.mainNode
      ?.append('text')
      .attr('x', 50 / 2 + 40)
      .attr('y', 50 / 2 - 2)
      .attr('dy', '.35em')
      .style('fill', status ? '#4F4F4F' : '#bdbdbd')
      .text(icon == 'action' ? 'Action' : this.getModel<any>().value.name);

    if (status === false) {
      d3.selectAll(this.getNode()).select('rect').style('stroke', '#bdbdbd').style('stroke-dasharray', '6');
      d3.selectAll(this.getNode())
        .select('image')
        .attr('xlink:href', actionDisable)
        .attr('width', 20)
        .attr('height', 20)
        .style('fill', 'red')
        .attr('x', 50 / 2 - 10)
        .attr('y', 50 / 2 - 10);
    }
  }

  public socketHighlight(type?: NodeType) {
    if (!this.isShadow() && type == NodeType.ACTION && (this.getModel() as any).parent.value.status) {
      d3.selectAll(this.getNode()).select('rect').style('stroke', '#2F9BFF').style('stroke-dasharray', '6');
    }
  }

  public socketUnHighlight(type?: NodeType, event?: NodeMouseEvent): void {
    if (!this.isShadow() && type == NodeType.ACTION && (this.getModel() as any).parent.value.status) {
      if ((this.getModel().value as any).status && event?.ctrlKey) {
        const link = ComponentUtil.handleLink(ComponentName.ACTION, (this.getModel().value as any).name, {
          template: (this.getModel().value as any).icon,
        });
        if (link) {
          window.open(link, '_blank');
        }
      }
      d3.selectAll(this.getNode()).select('rect').style('stroke', '#ADD8E6').style('stroke-dasharray', '0');
      const socketRect = this.getSocketBoundingRect();
      this.createDropdownMenu(socketRect);
    }
  }

  private getSocketBoundingRect() {
    const socketElement: any = d3.select(this.getNode()).select('rect').node();
    return socketElement.getBoundingClientRect();
  }

  private createDropdownMenu(socketRect: any) {
    if (this.menuOpen) return;
    this.menuOpen = true;
    this.workFlowView.setMenuOpen(this.menuOpen);
    const viewportHeight = window.innerHeight;
    const menuHeight = viewportHeight * 0.25;

    let left = socketRect.right + 20;
    let top = WorkflowLayoutUtil.calculateDropdownTop(socketRect.top, menuHeight, undefined, viewportHeight);

    const menuContainer = d3
      .select('body')
      .append('ul')
      .attr('id', 'action-menu')
      .attr('class', this.getModel<any>().value.name)
      .style('position', 'absolute')
      .style('left', left + 'px')
      .style('top', top + 'px')
      .style('background-color', 'white')
      .style('width', '250px')
      .style('height', '25%')
      .style('border', '1px solid #E0E4E8')
      .style('border-radius', '4px')
      .style('overflow-y', 'auto')
      .style('z-index', 1001);

    const header = menuContainer.append('div').style('background-color', '#F9FAFB').style('border-bottom', '1px solid #E0E4E8').style('padding', '8px');

    header
      .append('input')
      .attr('type', 'text')
      .attr('placeholder', 'Search')
      .style('width', 'calc(100% - 34px)')
      .style('border', '1px solid #E0E4E8')
      .style('border-radius', '4px')
      .style('padding', '8px')
      .on('input', function () {
        const searchText = this.value.trim().toLowerCase();
        menuContainer.selectAll('li').style('display', function () {
          const itemText = d3.select(this).text().toLowerCase();
          return itemText.includes(searchText) ? 'flex' : 'none';
        });
      });

    header
      .append('span')
      .html('&#128465;')
      .style('cursor', 'pointer')
      .style('margin-left', '10px')
      .style('font-size', '20px')
      .on('click', () => {
        this.closeDropdownMenu();
        this.deleteAction();
      });

    menuContainer.selectAll('li').style('padding', '10px').style('display', 'flex').style('align-items', 'center');

    (this.workFlowView as any).actionItems.map((action: ActionJsonModel) => {
      const listItem = menuContainer
        .append('li')
        .attr('data-testid', `select-item-${action.name}`)
        .style('display', 'flex')
        .style('font-size', '15px')
        .style('padding', '8px')
        .style('align-items', 'center');

      const svgImage = listItem.append('svg').attr('width', 16).attr('height', 16).style('margin-left', '6px');

      svgImage.append('svg:image').attr('xlink:href', this.getIcon(action.template)).attr('width', 16).attr('height', 16);

      listItem.append('span').style('margin-left', '6px').style('color', '#4F4F4F').text(action.name);

      listItem
        .on('mouseover', () => {
          listItem.style('background-color', '#F0F0F0').style('cursor', 'pointer');
        })
        .on('mouseout', () => {
          listItem.style('background-color', 'transparent').style('cursor', 'default');
        });
      listItem.on('click', () => {
        this.workFlowView.addActionItem(this.model as any, this.shadow, action.name, action.template);
        this.closeDropdownMenu();
      });
    });
    d3.select(document).on('pointerup', this.handleOutsideClick);
  }

  private deleteAction() {
    const clickedNodeId = this.getModel<any>().id;
    const status = (this.getModel() as any).value.status;
    if (status) {
      this.workFlowView.deleteActionItem(clickedNodeId);
    }
  }

  private closeDropdownMenu() {
    d3.select('#action-menu').remove();
    d3.select(document).on('pointerup', null);
    this.menuOpen = false;
    this.workFlowView.setMenuOpen(false);
    this.workFlowView.reRender();
  }

  private handleOutsideClick = (event: any) => {
    const menuContainer: any = d3.select(`#action-menu`);
    if (this.menuOpen && menuContainer && !menuContainer.node().contains(event.target)) {
      this.closeDropdownMenu();
    }
  };

  public setModel(model: Syntax<Token>): void {
    d3.select(this.el)
      .select('text')
      .text((model.value as any).name);
  }

  protected preSwap(target: View<Syntax>): boolean {
    switch (target.constructor) {
      case ActionNodeView:
        return true;
      default:
        return false;
    }
  }

  private getIcon = (iconName: string) => {
    switch (iconName) {
      case 'discord':
        return discord;
      case 'jira':
        return jira;
      case 'microsoft-teams':
        return microsoftTeams;
      case 'slack':
        return slack;
      case 'email':
        return email;
      case 'api':
        return api;
      default:
        return action;
    }
  };
}

export default ActionNodeView;
